"use client";

import { isI2VHistoryRow } from "@saas/image-to-video/types";
import { apiClient } from "@shared/lib/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { GenerationHistoryItem } from "./types";
import { GENERATION_HISTORY_QUERY_KEY } from "./types";

type TaskKind = GenerationHistoryItem["type"];

async function fetchTaskStatus(kind: TaskKind, id: string) {
	const response =
		kind === "video"
			? await apiClient["image-to-video"].status[":taskId"].$get({
					param: { taskId: id },
				})
			: await apiClient["image-generator"].status[":id"].$get({
					param: { id },
				});
	if (!response.ok) {
		return null;
	}
	return ((await response.json()) as { status: string }).status;
}

/**
 * History 是图片和视频合在一起的，但每个页面只接着查自己那一类没出完的任务。任务没有
 * webhook，只在有人调 /status 时才结算：在视频页出片后切到生图页（反过来也一样），就没人
 * 查了，卡片一直转到 5 分钟一次的 sweep cron 收尾（10/23 Basic 片上游 44 秒出完，生图页
 * 转了 5 分钟）。这里替页面查另一类，出完刷新 History 和积分。
 * 老 ai-video 的视频行 image-to-video 的 /status 不认，照旧由 cron 收尾。
 */
export function useResumeHistoryTasks(
	items: GenerationHistoryItem[],
	kind: TaskKind,
) {
	const queryClient = useQueryClient();
	const taskIds = items
		.filter(
			(item) =>
				item.type === kind &&
				(item.status === "GENERATING" ||
					item.status === "PENDING" ||
					item.status === "PROCESSING") &&
				(item.type === "image" || isI2VHistoryRow(item.metadata)),
		)
		.map((item) => item.id);

	useQuery({
		queryKey: ["resume-history-tasks", kind, taskIds],
		queryFn: async () => {
			const statuses = await Promise.all(
				taskIds.map((id) =>
					fetchTaskStatus(kind, id).catch(() => null),
				),
			);
			if (
				statuses.some(
					(status) => status === "SUCCESS" || status === "FAILED",
				)
			) {
				await queryClient.invalidateQueries({
					queryKey: GENERATION_HISTORY_QUERY_KEY,
				});
				queryClient.invalidateQueries({ queryKey: ["credits"] });
			}
			return statuses;
		},
		enabled: taskIds.length > 0,
		refetchInterval: 3000,
		// 和视频页一样：后台标签页默认停轮询，切回来才结算
		refetchIntervalInBackground: true,
	});
}
